import { Link, useLocation } from "@tanstack/react-router";
import { Icon } from "#/components/Icon.tsx";
import { ThemeToggle } from "#/components/ThemeToggle.tsx";
import { BrandMark } from "#/components/BrandMark.tsx";
import type { Messages } from "#/i18n/index.ts";
import { LOCALES, LOCALE_NAMES, LANGUAGE_TAGS, localeParam, localizedPath, type Locale } from "#/lib/locale.ts";
import { REPOSITORY_URL } from "#/lib/site.ts";
import type { Theme } from "#/lib/theme.ts";

/** The five primary sections, in navigation order. */
const SECTIONS = [
  { to: "/{-$locale}/docs", key: "docs" },
  { to: "/{-$locale}/commands", key: "commands" },
  { to: "/{-$locale}/packages", key: "packages" },
  { to: "/{-$locale}/hosted", key: "hosted" },
  { to: "/{-$locale}/changelog", key: "changelog" },
] as const;

/**
 * The primary section links, used by both the wide navigation and the menu.
 *
 * @param props - Active locale and copy.
 * @returns One link per section; the current section is marked by the router.
 */
function SectionLinks({ locale, messages }: { locale: Locale; messages: Messages }) {
  return SECTIONS.map((section) => (
    <Link key={section.key} to={section.to} params={{ locale: localeParam(locale) }}>
      {messages.nav[section.key]}
    </Link>
  ));
}

/**
 * Site header: wordmark, primary navigation, and the GitHub, language and
 * theme controls. Below 768 px the navigation folds into a native disclosure,
 * so it works before the page's script has loaded.
 *
 * @param props - Active locale, its copy, and the theme the page rendered with.
 * @returns The page header.
 */
export function SiteHeader({ locale, messages, theme }: { locale: Locale; messages: Messages; theme: Theme }) {
  const { pathname } = useLocation();
  return (
    <header className="site-header">
      <a className="skip-link" href="#content">
        {messages.a11y.skip}
      </a>
      <div className="page site-header__bar">
        <Link to="/{-$locale}" params={{ locale: localeParam(locale) }} className="wordmark" aria-label={messages.meta.siteName}>
          <BrandMark /><span className="lockup-caption" translate="no">CLI</span>
        </Link>
        <nav className="site-nav" aria-label={messages.a11y.mainNav}>
          <SectionLinks locale={locale} messages={messages} />
        </nav>
        <details className="site-menu">
          <summary>{messages.a11y.menu}</summary>
          <nav aria-label={messages.a11y.mainNav}>
            <SectionLinks locale={locale} messages={messages} />
          </nav>
        </details>
        <div className="site-tools">
          <details className="language-menu">
            <summary className="icon-button" aria-label={messages.language.label} title={messages.language.label}>
              <Icon name="languages" />
            </summary>
            <ul>
              {LOCALES.map((option) => (
                <li key={option}>
                  <a
                    href={localizedPath(pathname, option)}
                    hrefLang={LANGUAGE_TAGS[option]}
                    lang={LANGUAGE_TAGS[option]}
                    aria-current={option === locale ? "true" : undefined}
                  >
                    {LOCALE_NAMES[option]}
                  </a>
                </li>
              ))}
            </ul>
          </details>
          <ThemeToggle initial={theme} messages={messages} />
          <a className="icon-button hide-narrow" href={REPOSITORY_URL} aria-label={messages.nav.github}>
            <Icon name="github" />
          </a>
        </div>
      </div>
    </header>
  );
}
